import { Flex, SidePanel, Spinner } from "@wayflyer/flyui";
import { type ReactNode, Suspense } from "react";

import type { AuthCallbackPath } from "./callback-paths";
import { useConnectionFlowsConfig } from "./config";
import {
  AmazonConnect,
  BigcommerceConnectOtp,
  CodatConnect,
  GoogleAdsConnect,
  GoogleAnalyticsConnect,
  MagentoConnect,
  MetaAdsConnect,
  MockifyConnect,
  NetsuiteConnect,
  QuickbooksConnect,
  ShopifyAppStoreConnect,
  ShopifyConnect,
  StripeConnect,
  TikTokConnect,
  WooCommerceConnect,
  XeroConnect,
} from "./connectors";

const createConnectorComponent = (
  slug: string,
  {
    onStarted,
    postConnectPath,
    codatFallbackPlatformName,
  }: { onStarted: () => void; postConnectPath: AuthCallbackPath; codatFallbackPlatformName?: string }
): ReactNode => {
  const components: Record<string, ReactNode> = {
    shopify2: <ShopifyConnect postConnectPath={postConnectPath} onInitiateConnection={onStarted} />,
    "shopify-appstore": (
      <ShopifyAppStoreConnect postConnectPath={postConnectPath} fullWidthImage onInitiateConnection={onStarted} />
    ),
    amazonrutter: <AmazonConnect postConnectPath={postConnectPath} onInitiateConnection={onStarted} />,
    stripe2: <StripeConnect onInitiateConnection={onStarted} />,
    woocommerce2: <WooCommerceConnect postConnectPath={postConnectPath} onInitiateConnection={onStarted} />,
    bigcommercerutter: <BigcommerceConnectOtp postConnectPath={postConnectPath} onInitiateConnection={onStarted} />,
    magento2: <MagentoConnect postConnectPath={postConnectPath} onInitiateConnection={onStarted} />,
    mockify: <MockifyConnect postConnectPath={postConnectPath} onInitiateConnection={onStarted} />,
    ga: <GoogleAnalyticsConnect onInitiateConnection={onStarted} />,
    google_ads2: <GoogleAdsConnect onInitiateConnection={onStarted} />,
    facebook2: <MetaAdsConnect onInitiateConnection={onStarted} />,
    tiktok: <TikTokConnect onInitiateConnection={onStarted} />,
    codat_accounting: (
      <CodatConnect onInitiateConnection={onStarted} fallbackPlatformName={codatFallbackPlatformName} />
    ),
    xero: <XeroConnect onInitiateConnection={onStarted} />,
    netsuite: <NetsuiteConnect onInitiateConnection={onStarted} />,
    quickbooks: <QuickbooksConnect onInitiateConnection={onStarted} />,
  };
  return components[slug] ?? null;
};

export interface ConnectionSidePanelProps {
  /** Connector slug to render; null when nothing is selected. */
  platform: string | null;
  /** Whether the panel is open. */
  open: boolean;
  /** Panel title. */
  title: string;
  onClose: () => void;
  /** Primary (footer) action; omit for a panel with no primary action. */
  primaryAction?: { content: string; onAction: () => void };
  /** Called when a flow initiates a connection, with the connector slug (for analytics). */
  onConnectionStarted?: (slug: string) => void;
  /**
   * Render a custom body for a slug instead of the built-in connector flow (e.g.
   * funding-application's accounting document upload). Return `undefined` to fall
   * back to the connector registry.
   */
  renderPanel?: (slug: string) => ReactNode | undefined;
  /** Legacy fallback platform name for the Codat flow. */
  codatFallbackPlatformName?: string;
}

/**
 * Side panel that hosts the third-party connector flows. Given a connector
 * `slug`, it renders the matching `*Connect` flow (which reads its endpoints and
 * callback path from `ConnectionFlowsProvider`). Callers own the open/title/done
 * logic and may inject bespoke panels via `renderPanel`.
 */
export function ConnectionSidePanel({
  platform,
  open,
  title,
  onClose,
  primaryAction,
  onConnectionStarted,
  renderPanel,
  codatFallbackPlatformName,
}: ConnectionSidePanelProps) {
  const { defaultCallbackPath } = useConnectionFlowsConfig();
  const customPanel = platform ? renderPanel?.(platform) : undefined;

  return (
    <SidePanel
      open={open}
      isModal={false}
      onClose={onClose}
      title={title}
      closeOnClickElseWhere={false}
      variant="md"
      primaryAction={primaryAction}
    >
      {platform ? (
        <Suspense
          fallback={
            <Flex justify="center" align="center" height="100%">
              <Spinner size={32} />
            </Flex>
          }
        >
          {customPanel ??
            createConnectorComponent(platform, {
              onStarted: () => onConnectionStarted?.(platform),
              postConnectPath: defaultCallbackPath,
              codatFallbackPlatformName,
            })}
        </Suspense>
      ) : null}
    </SidePanel>
  );
}
